import { HttpClient } from '@angular/common/http';
import {
  EnvironmentProviders,
  Injectable,
  inject,
  isDevMode,
  provideEnvironmentInitializer,
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NavigationError, Router } from '@angular/router';
import {
  catchError,
  exhaustMap,
  filter,
  fromEvent,
  map,
  merge,
  of,
  timer,
} from 'rxjs';

/**
 * Provides the version check at the environment level.
 * Import this in the app config to start polling for deploys on bootstrap.
 */
export const provideVersionCheck = (): EnvironmentProviders => {
  return provideEnvironmentInitializer(() =>
    inject(VersionCheckService).start(),
  );
};

/** How often to compare the running build against the deployed one. */
const CHECK_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Build stamp CI substitutes into index.html and version.json
 * (see the "Stamp the build" step in .gitlab-ci.yml).
 */
const VERSION_PLACEHOLDER = '%CONSOLE_VERSION%';

/** Shape of the version.json the CI build writes next to index.html. */
interface DeployedVersion {
  commit: string;
  builtAt?: string;
}

/**
 * Browser-specific messages for a failed dynamic `import()` — Chrome,
 * Firefox and Safari each word it differently.
 */
const CHUNK_LOAD_ERROR_MESSAGE =
  /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed/i;

const isChunkLoadError = (error: unknown): boolean => {
  return error instanceof Error && CHUNK_LOAD_ERROR_MESSAGE.test(error.message);
};

/**
 * Detects console deploys while the app is open and prompts the user to
 * reload instead of requiring a manual hard refresh.
 *
 * The deploy serves index.html with `no-cache` but every hashed bundle as
 * immutable, so a long-lived tab keeps running the build it started with.
 * This service compares the commit baked into the served index.html (meta
 * tag `console-version`) against the deployed version.json — on an interval,
 * when the tab becomes visible again, and immediately when a lazy chunk
 * fails to load (the chunk's hashed file was replaced by a deploy). On a
 * mismatch it shows a persistent "Reload" snackbar.
 */
@Injectable({
  providedIn: 'root',
})
export class VersionCheckService {
  private _http = inject(HttpClient);
  private _snackBar = inject(MatSnackBar);
  private _router = inject(Router);

  /** Commit the running bundle was built from. */
  private _runningCommit: string | null = null;

  /** Whether the "new version" snackbar is currently on screen. */
  private _notifying = false;

  /**
   * Start watching for deploys. No-op in dev mode — local builds have no
   * version.json and reload via HMR anyway.
   */
  public start(): void {
    if (isDevMode()) {
      return;
    }

    this._runningCommit = this._readRunningCommit();

    const becameVisible$ = fromEvent(document, 'visibilitychange').pipe(
      filter(() => document.visibilityState === 'visible'),
    );

    // A failed lazy-chunk import usually means a deploy replaced the hashed
    // files this bundle references — verify right away instead of waiting
    // for the next interval. (If it was a network blip instead, the
    // version.json fetch fails too and no prompt is shown.)
    const chunkLoadFailed$ = this._router.events.pipe(
      filter(
        (event): event is NavigationError =>
          event instanceof NavigationError && isChunkLoadError(event.error),
      ),
    );

    merge(timer(0, CHECK_INTERVAL_MS), becameVisible$, chunkLoadFailed$)
      .pipe(
        filter(
          () => !this._notifying && document.visibilityState === 'visible',
        ),
        exhaustMap(() => this._fetchDeployedVersion()),
        filter((version): version is DeployedVersion => version !== null),
      )
      .subscribe((version) => this._compare(version.commit));
  }

  /**
   * Read the commit the served index.html was stamped with. Falls back to
   * null (baseline taken from the first successful fetch instead) when the
   * placeholder was never substituted, e.g. a local production-config build.
   */
  private _readRunningCommit(): string | null {
    const content = document
      .querySelector('meta[name="console-version"]')
      ?.getAttribute('content');

    if (!content || content === VERSION_PLACEHOLDER) {
      return null;
    }

    return content;
  }

  /**
   * Fetch the deployed version stamp. The query parameter defeats the
   * CDN's long-lived cache headers — a cached answer would defeat the
   * whole check. Errors and malformed bodies (e.g. the SPA fallback
   * serving index.html for a missing version.json) resolve to null.
   */
  private _fetchDeployedVersion() {
    return this._http.get<DeployedVersion>(`version.json?t=${Date.now()}`).pipe(
      map((version) => (typeof version?.commit === 'string' ? version : null)),
      catchError(() => of(null)),
    );
  }

  private _compare(deployedCommit: string): void {
    if (this._runningCommit === null) {
      this._runningCommit = deployedCommit;
      return;
    }

    if (deployedCommit !== this._runningCommit) {
      this._notifyUpdateAvailable();
    }
  }

  private _notifyUpdateAvailable(): void {
    this._notifying = true;

    const snackBar = this._snackBar.open(
      'A new version of the console is available.',
      'Reload',
      {
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
      },
    );

    snackBar.onAction().subscribe(() => window.location.reload());

    // Another toast can replace this one (MatSnackBar shows one at a time).
    // Clearing the flag lets the next check re-surface the notice.
    snackBar.afterDismissed().subscribe(() => (this._notifying = false));
  }
}
